import { AppSelect } from './ui/AppSelect';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Check,
  Copy,
  Download,
  Image as ImageIcon,
  Loader2,
  Play,
  RefreshCw,
  Search,
  Sparkles,
  Swords,
  Upload,
  Video,
  X,
  Zap,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useToast } from "../context/ToastContext";
import { authenticatedFetch } from "../utils/api";
import { API_HOST } from "../utils/constants";
import { runTaskToCompletion } from "../services/taskService";
import { startPolling, submitVideoTask } from "../services/videoService";
import {
  getProjectDefaultVideoModel,
  getProjectDefaultVideoRatio,
  getProjectVideoProvider,
  getPublicVideoLabel,
  getVideoProviderLabel,
  isOverseasVideoProvider,
  isSeedanceVideoProvider,
  normalizeVideoProvider,
  VIDEO_PROVIDER_OMINI_SEEDANCE,
} from "../utils/projectRuntime";
import { getEffectiveAssetMeta } from "../utils/guaVideoAssetStatus";
import { preflightVideoAssetContents } from "../utils/videoAssetPreflight";
import { getDreaminaProxyOriginalUrl, isDreaminaProxySeedance20Provider, requireDreaminaProxyOriginalUrl } from "../utils/dreaminaProxyReferences";
import { useWorkshopImageSettings } from "../hooks/useWorkshopImageSettings";
import { getGlobalProviderRecommendation, normalizeTextProvider, resolveDatastartWorkflowTextProvider } from "../constants/globalAiProviders";
import { DATASTART_IMAGE_MODELS, getCompatibleImageAspectOptions, isDatastartImageProvider, normalizeDatastartImageAspectRatio } from '../constants/datastartImageModels';
import { ASTRAFLOW_IMAGE_MODELS, isAstraflowImageProvider } from '../constants/astraflowImageModels';
import { ASTRAFLOW_ASTRA_MODELS } from '../constants/astraflowAstraModels';
import {
  assertWanxImageInputLimit,
} from "../utils/wanxImageCapabilities";

// color 为供应商线路分组标识色（类品牌色，跨主题保持一致），保留
const IMAGE_MODEL_OPTIONS = [
  { id: "wangsugem", label: "Gemini_线路1", provider: "wangsugem", color: "#22c55e" },
  { id: "wangsugpt", label: "Image-2_线路1", provider: "WANGSUGPT", color: "#22c55e" },
  { id: "huobao_gpt", label: "Image-2_线路2", provider: "HUOBAO_GPT", color: "#fb923c" },
  { id: "huobao_gpt_image_cheap", label: "Image-2_线路2-4K", provider: "HUOBAO_GPT_IMAGE_CHEAP", color: "#fb923c" },
  { id: "astraflow_gemini", label: "Gemini_线路3", provider: "ASTRAFLOW_GEMINI", color: "#a78bfa" },
  { id: "astraflow_openai", label: "Image-2_线路3", provider: "ASTRAFLOW_OPENAI", color: "#a78bfa" },
  { id: "astraflow_openai_luna", label: "Image-2 + Luna_线路3", provider: "ASTRAFLOW_OPENAI_LUNA", color: "#a78bfa" },
  { id: "astraflow_openai_terra", label: "Image-2 + Terra_线路3", provider: "ASTRAFLOW_OPENAI_TERRA", color: "#a78bfa" },
  { id: "astraflow_openai_sol", label: "Image-2 + Sol_线路3", provider: "ASTRAFLOW_OPENAI_SOL", color: "#a78bfa" },
  ...ASTRAFLOW_ASTRA_MODELS.map(({ id, label, provider, model }) => ({ id, label, provider, imageModel: model, color: '#a78bfa' })),
  ...ASTRAFLOW_IMAGE_MODELS.map(({ id, label, provider }) => ({ id, label, provider, color: '#a78bfa' })),
  { id: "astraflow_grok", label: "Grok Imagine_线路3", provider: "ASTRAFLOW_GROK", color: "#a78bfa" },
  { id: "claude_opus_mj", label: "Claude Opus + MJ", provider: "CLAUDE_OPUS_MJ", color: "#c084fc" },
  { id: "doubao", label: "豆包", provider: "DOUBAO", color: "#34d399" },
  { id: "wanx", label: "千问图像3.0 Pro", provider: "WANX", color: "#818cf8" },
  ...DATASTART_IMAGE_MODELS.map(({ id, label, provider }) => ({ id, label, provider, color: '#38bdf8' })),
].map((option) => ({
  ...option,
  recommendation: getGlobalProviderRecommendation(option.id),
}));

const ASSET_TYPE_OPTIONS = [
  {
    value: "technique",
    label: "招式",
    description: "固定人物起势、动作路径、命中和收招，适合拳脚、兵器、身法。",
  },
  {
    value: "effect",
    label: "特效",
    description: "固定颜色、材质、源头、扩散、爆点和消散，适合法术、爆炸、能量。",
  },
  {
    value: "combo",
    label: "招式+特效",
    description: "固定人物动作和能量轨迹的绑定关系，适合功夫片、玄幻战斗。",
  },
];

const RULE_TEMPLATES = {
  technique: {
    assetName: "虎形突袭",
    visualIdentity: "低身前压，肩背像猛虎伏地蓄力，出手瞬间有弧形速度线，衣摆和尘土向后拖拽。",
    motionRule: "起势为重心下沉，前脚踏地蓄力；第二拍身体斜冲；第三拍爪形手/武器从下往上撕开空间；最后收在半蹲回身姿态。",
    impactRule: "命中点必须有清楚受力方向，对手身体后仰或侧飞，地面出现抓痕、尘土和空气震荡。",
    cameraRule: "低机位广角进入，中段侧向跟拍，爆点用近景或强透视，收招用中远景交代站位。",
    avoidRule: "不要直挺挺站立出招；不要每格同一姿态；不要只画角色摆拍而没有身体重心和受力反馈。",
  },
  effect: {
    assetName: "雷云剑阵",
    visualIdentity: "蓝白雷电从剑尖升空，形成旋转云涡，电弧沿地面和墙面扩散，爆点有强烈白色核心。",
    motionRule: "第一拍能量在武器或手势处汇聚；第二拍射向空中；第三拍云层打开；第四拍多道雷电落下；最后电弧逐渐消散。",
    impactRule: "雷电命中后地面焦黑、碎石弹起、空气发白，对手被冲击波推开，环境光短暂变冷。",
    cameraRule: "先用手部/武器近景，再切广角看云层与地面关系，爆点可用仰拍或俯拍路线图。",
    avoidRule: "不要只有一团光；不要把雷电画成静态装饰；不要出现文字、符号水印或不可理解的乱码符文。",
  },
  combo: {
    assetName: "风雷破阵斩",
    visualIdentity: "人物挥剑时蓝白雷电缠绕剑身，外圈有螺旋风压，剑气轨迹像半月形弧线切开空气。",
    motionRule: "起势为握剑后撤蓄力，眼神锁定目标；挥剑时身体旋转带动剑气；剑气离体后沿半月轨迹推进；收招时衣摆和发丝仍被风压拉动。",
    impactRule: "剑气命中前空气被压缩，命中点爆出电弧和风刃，对手或障碍物被斜向切开并后退。",
    cameraRule: "起势用中近景，挥剑用侧向跟拍，剑气推进用广角透视，命中用低机位冲击构图。",
    avoidRule: "不要人物动作和特效脱节；不要只让特效自己飞；不要让能量轨迹每格变形到无法识别。",
  },
};

const EFFECT_MODE_OPTIONS = [
  {
    value: "fight",
    label: "打戏动作",
    description: "拳脚、兵器、追打、摔投、压制",
    rules: [
      "动作必须有清晰起势、接触点、受力方向、失衡或反击结果。",
      "强调身体重心、脚步路径、速度线、衣摆和残影方向。",
      "每个关键格都要能看出上一拍如何进入下一拍。",
    ],
  },
  {
    value: "vfx",
    label: "特效爆点",
    description: "爆炸、碎裂、冲击波、空间撕裂",
    rules: [
      "写清特效源头、扩散路径、爆点中心、碎片方向和环境反馈。",
      "特效要和地面、墙面、衣物、烟尘、光影产生互动。",
      "避免只写炫酷，要让视频模型能理解特效发生顺序。",
    ],
  },
  {
    value: "spell",
    label: "法术能量",
    description: "结印、阵法、光束、符文、召唤",
    rules: [
      "写清能量从哪里出现，如何汇聚、释放、命中、消散。",
      "法术形态要有材质感，例如电弧、火焰、冰雾、墨线、金色粒子。",
      "人物动作和法术轨迹必须绑定，不要只有光效没有施法动作。",
    ],
  },
  {
    value: "speed",
    label: "速度追逐",
    description: "高速移动、追车、飞跃、闪现",
    rules: [
      "通过运动模糊、速度线、残影、前后景拉伸表现速度。",
      "写清移动方向、障碍物、追逐距离和镜头跟随方式。",
      "不要让角色漂浮，脚步、轮胎、衣物或空气扰动要有物理反馈。",
    ],
  },
  {
    value: "impact",
    label: "重击破坏",
    description: "砸墙、落地、撞击、掀飞",
    rules: [
      "突出冲击前一秒的蓄力、撞击瞬间和冲击后的余波。",
      "碎石、灰尘、裂纹、衣物和角色姿态要体现力量方向。",
      "镜头可用低机位、广角透视、近景冲击或俯视路线图。",
    ],
  },
  {
    value: "emotion_power",
    label: "情绪爆发",
    description: "怒气觉醒、压迫感、反转高光",
    rules: [
      "人物先有下意识微反应，再进入爆发动作。",
      "光线、构图、特效和动作都服务于情绪落点。",
      "避免机械摆拍，必须有表情、呼吸、手部或身体的小变化。",
    ],
  },
];

const REFERENCE_STYLE_OPTIONS = [
  {
    value: "3x3",
    label: "3x3过程板",
    aspectRatio: "1:1",
    description: "九宫格，适合连续动作、法术释放和特效过程。",
    prompt: "3x3九宫格连续参考图，九个画格从左上到右下推进同一个动作/特效过程，每格独立边框，每格左上角必须有清晰小号镜号 01-09，黑白漫画线稿，白底，强透视，速度线、冲击线、能量轨迹清晰。",
  },
  {
    value: "4x3",
    label: "12格导演板",
    aspectRatio: "16:9",
    description: "十二格，更适合复杂打斗、追逐和多爆点调度。",
    prompt: "4x3十二格动作/特效导演板，左上到右下阅读顺序，每格左上角必须有清晰小号镜号 01-12，黑白漫画线稿，白底，强透视，密集速度线、冲击线、残影、能量轨迹和动态分镜边框。",
  },
  {
    value: "keyframe",
    label: "单张关键帧",
    aspectRatio: "16:9",
    description: "单张高冲击参考图，适合特效爆点和法术定格。",
    prompt: "单张电影级动作/特效关键帧参考图，黑白漫画线稿，强透视，高冲击构图，主体动作、特效路径、爆点中心、环境反馈和光影方向清晰。",
  },
];

const ANNOTATION_MODE_OPTIONS = [
  { value: "clean", label: "干净参考图", description: "不生成文字标注，最适合直接喂给视频模型。" },
  { value: "director", label: "导演标注图", description: "允许少量箭头、节奏圈、timing/camera note，适合人看和二次调整。" },
];

const RIGHT_TABS = [
  { id: "assets", label: "参考资产" },
  { id: "saved", label: "已保存" },
  { id: "history", label: "生成历史" },
];

const DOMESTIC_VIDEO_MODEL_OPTIONS = [
  { value: "doubao-seedance-2-0-fast-260128", label: "GuaVideo Fast" },
  { value: "doubao-seedance-2-0-260128", label: "GuaVideo" },
];
const OVERSEAS_VIDEO_MODEL_OPTIONS = [
  { value: "dreamina-seedance-2-0-fast", label: "Dreamina 2.0 Fast" },
  { value: "dreamina-seedance-2-0", label: "Dreamina 2.0" },
];
const GUA_OMINI_VIDEO_MODEL_OPTIONS = [
  { value: "dreamina-seedance-2-0-fast", label: "GuaOmini 2.0 Fast" },
  { value: "dreamina-seedance-2-0", label: "GuaOmini 2.0" },
];
const VIDEO_RATIO_OPTIONS = ["9:16", "16:9", "21:9", "1:1"];
const VIDEO_RESOLUTION_OPTIONS = ["480p", "720p"];
const VIDEO_DURATION_OPTIONS = [5, 10, 15];

const normalizeText = (value) => String(value || "").trim();

const assetImageUrl = (asset) => asset?.imageUrl || asset?.url || asset?.thumbnailUrl || "";

const variantImageUrl = (variant) => {
  if (!variant) return "";
  if (typeof variant === "string") return variant;
  return variant.imageUrl || variant.url || variant.thumbnailUrl || "";
};

const safeFileName = (value) => normalizeText(value).replace(/[\\/:*?"<>|]+/g, "_") || "effect-reference";

const summarizePromptTitle = (value, fallback = "招式参考") => {
  const text = normalizeText(value).replace(/\s+/g, " ");
  if (!text) return fallback;
  return text.length > 24 ? `${text.slice(0, 24)}...` : text;
};

const buildFixedRuleText = ({
  assetType,
  assetName,
  ownerName,
  visualIdentity,
  motionRule,
  impactRule,
  cameraRule,
  avoidRule,
}) => {
  const typeLabel = ASSET_TYPE_OPTIONS.find(option => option.value === assetType)?.label || "招式+特效";
  const lines = [
    `类型：${typeLabel}`,
    `名称：${normalizeText(assetName) || "未命名招式/特效"}`,
    ownerName ? `归属/使用者：${ownerName}` : "",
    visualIdentity ? `稳定视觉识别：${visualIdentity}` : "",
    motionRule ? `动作/发生规则：${motionRule}` : "",
    impactRule ? `命中/爆点规则：${impactRule}` : "",
    cameraRule ? `镜头规则：${cameraRule}` : "",
    avoidRule ? `禁忌规则：${avoidRule}` : "",
  ].filter(Boolean);
  return lines.join("\n");
};

const extractGeneratedImageUrl = (result, fallbackUrl) => {
  if (fallbackUrl) return fallbackUrl;
  if (!result) return null;
  if (typeof result === "string") return result.startsWith("http") ? result : null;
  if (Array.isArray(result)) {
    for (const item of result) {
      const url = extractGeneratedImageUrl(item);
      if (url) return url;
    }
    return null;
  }
  if (typeof result === "object") {
    const direct = result.imageUrl || result.url || result.resultUrl || result.videoUrl;
    if (typeof direct === "string" && direct.startsWith("http")) return direct;
    if (Array.isArray(result.imageUrls) && result.imageUrls.length > 0) return result.imageUrls[0];
    if (result.result) return extractGeneratedImageUrl(result.result);
  }
  return null;
};

const extractExpandedPrompt = (result) => {
  if (!result) return "";
  if (typeof result === "string") return result.trim();
  if (typeof result === "object") {
    return normalizeText(result.prompt || result.expandedPrompt || result.text || result.result);
  }
  return "";
};

const extractVideoUrl = (data) => {
  if (!data) return "";
  if (typeof data === "string") return data.startsWith("http") ? data : "";
  return data.videoUrl
    || data.resultUrl
    || data.url
    || data.result?.videoUrl
    || data.result?.video_url
    || data.output?.videoUrl
    || data.output?.video_url
    || "";
};

const parseMetadata = (metadata) => {
  if (!metadata) return {};
  if (typeof metadata === "object") return metadata;
  try {
    return JSON.parse(metadata);
  } catch {
    return {};
  }
};

const buildAssetOptions = (characters, scenes, props) => {
  const result = [];
  const pushAsset = (asset, typeLabel, typeKey) => {
    const mainUrl = assetImageUrl(asset);
    if (mainUrl) {
      result.push({
        key: `${typeKey}-${asset.id}-main`,
        sourceId: asset.id,
        typeKey,
        typeLabel,
        name: asset.name || typeLabel,
        description: asset.description || "",
        url: mainUrl,
        originalUrl: getDreaminaProxyOriginalUrl(asset),
        guaVideoAssetId: asset.guaVideoAssetId || asset.assetId || "",
        guaVideoAssetStatus: asset.guaVideoAssetStatus || "",
        engGuaVideoAssetId: asset.engGuaVideoAssetId || "",
        engGuaVideoAssetStatus: asset.engGuaVideoAssetStatus || "",
        guaOminiAssetId: asset.guaOminiAssetId || "",
        guaOminiAssetStatus: asset.guaOminiAssetStatus || "",
        dreaminaProxyAssetId: asset.dreaminaProxyAssetId || "",
        dreaminaProxyAssetStatus: asset.dreaminaProxyAssetStatus || "",
        isVariant: false,
      });
    }
    (asset.variations || []).forEach((variant, index) => {
      const url = variantImageUrl(variant);
      if (!url) return;
      const variantName = variant?.name || variant?.label || `${asset.name || typeLabel} 变体${index + 1}`;
      result.push({
        key: `${typeKey}-${asset.id}-variant-${index}`,
        sourceId: asset.id,
        typeKey,
        typeLabel,
        name: variantName,
        parentName: asset.name || typeLabel,
        description: variant?.description || asset.description || "",
        url,
        originalUrl: getDreaminaProxyOriginalUrl(variant),
        guaVideoAssetId: variant?.guaVideoAssetId || variant?.assetId || "",
        guaVideoAssetStatus: variant?.guaVideoAssetStatus || "",
        engGuaVideoAssetId: variant?.engGuaVideoAssetId || "",
        engGuaVideoAssetStatus: variant?.engGuaVideoAssetStatus || "",
        guaOminiAssetId: variant?.guaOminiAssetId || "",
        guaOminiAssetStatus: variant?.guaOminiAssetStatus || "",
        dreaminaProxyAssetId: variant?.dreaminaProxyAssetId || "",
        dreaminaProxyAssetStatus: variant?.dreaminaProxyAssetStatus || "",
        isVariant: true,
      });
    });
  };

  (characters || []).forEach(asset => pushAsset(asset, "角色", "character"));
  (scenes || []).forEach(asset => pushAsset(asset, "场景", "scene"));
  (props || []).forEach(asset => pushAsset(asset, "道具", "prop"));
  return result;
};

const buildReferencePrompt = ({
  promptText,
  fixedRuleText,
  mode,
  referenceStyle,
  annotationMode,
  selectedRefs,
  localMediaRefs = [],
}) => {
  const modeConfig = EFFECT_MODE_OPTIONS.find(option => option.value === mode) || EFFECT_MODE_OPTIONS[0];
  const styleConfig = REFERENCE_STYLE_OPTIONS.find(option => option.value === referenceStyle) || REFERENCE_STYLE_OPTIONS[0];
  const isDirector = annotationMode === "director";
  const isMultiPanel = referenceStyle !== "keyframe";
  const imageRefs = localMediaRefs.filter(ref => ref.kind === "image");
  const refRows = [
    ...selectedRefs.map((ref, index) => `${index + 1}. ${ref.typeLabel}：${ref.name}${ref.parentName ? `（属于 ${ref.parentName}）` : ""}`),
    ...imageRefs.map((ref, index) => `${selectedRefs.length + index + 1}. 上传参考图：${ref.name || "用户上传图片"}`),
  ];
  const refText = refRows.length
    ? refRows.join("\n")
    : "无参考图，按文字描述生成。";
  const panelRules = referenceStyle === "keyframe"
    ? [
        "画面要求：必须有一个明确视觉爆点；动作、特效、光影和环境反馈集中在同一瞬间。",
        "空间要求：前景、中景、背景层次清楚；主体和特效不能糊成一团。",
      ]
    : [
        "连续性要求：每格必须承接上一格的动作方向、人物站位和能量/冲击路径。",
        "节奏要求：至少包含起势、推进、爆点、余波或收束，不要每格都是同一种姿态。",
        "空间要求：地面、墙面、障碍物或空气扰动要帮助判断速度和受力方向。",
      ];
  const numberingRule = isMultiPanel
    ? referenceStyle === "4x3"
      ? "镜号要求：必须按阅读顺序在每格左上角标注 01、02、03...12；编号要小而清楚，贴近画格内边距，不能遮挡人物、特效或速度线。"
      : "镜号要求：必须按阅读顺序在每格左上角标注 01、02、03...09；编号要小而清楚，贴近画格内边距，不能遮挡人物、特效或速度线。"
    : "";

  return [
    `${modeConfig.label}参考图，${styleConfig.prompt}`,
    isDirector
      ? isMultiPanel
        ? "允许少量红色/蓝色手绘箭头、速度标记、节奏圈、impact burst、timing/camera note；每格左上角镜号必须保留且清晰；标注必须辅助动作，不要出现对白、字幕、水印、logo 或无关文字。"
        : "允许少量红色/蓝色手绘箭头、速度标记、节奏圈、impact burst、timing/camera note；标注必须辅助动作，不要出现对白、字幕、水印、logo 或无关文字。"
      : isMultiPanel
        ? "干净视频参考版：除每格左上角的小号镜号编号外，不要任何文字、对白框、拟声词、字幕、水印、logo；只保留动作、速度线、冲击线、能量轨迹、构图和站位。"
        : "干净视频参考版：不要任何文字、数字、对白框、拟声词、字幕、水印、logo；只保留动作、速度线、冲击线、能量轨迹、构图和站位。",
    "",
    "用途：给视频模型作为动作/特效/法术参考。重点不是角色脸，而是人物站位、动作路径、特效路径、受力方向、镜头切换和速度感。",
    "",
    "用户核心描述：",
    normalizeText(promptText) || "双方高速交手，能量爆点撕开空间，最后停在可接下一镜的位置。",
    "",
    "固定招式/特效资产规则（必须严格保持一致）：",
    normalizeText(fixedRuleText) || "暂无固定规则，按用户核心描述生成。",
    "",
    "模式增强要求：",
    ...modeConfig.rules.map(rule => `- ${rule}`),
    "",
    "参考图绑定：",
    refText,
    "",
    "生成结构要求：",
    ...panelRules.map(rule => `- ${rule}`),
    ...(numberingRule ? [`- ${numberingRule}`] : []),
    "",
    "风格限制：黑白漫画线稿，白底，强透视，动态构图，线条清楚；人物脸部简化，不出现真实人脸；如果绑定角色参考图，只保持体型、服装轮廓和动作关系。",
  ].join("\n");
};

const buildVideoPrompt = ({ promptText, fixedRuleText, mode, referenceStyle, annotationMode, selectedRefs, hasReferenceBoard = true, localMediaRefs = [] }) => {
  const modeConfig = EFFECT_MODE_OPTIONS.find(option => option.value === mode) || EFFECT_MODE_OPTIONS[0];
  const styleConfig = REFERENCE_STYLE_OPTIONS.find(option => option.value === referenceStyle) || REFERENCE_STYLE_OPTIONS[0];
  const localImages = localMediaRefs.filter(ref => ref.kind === "image");
  const localVideos = localMediaRefs.filter(ref => ref.kind === "video");
  const firstRefText = hasReferenceBoard
    ? ["@图片1 是当前黑白动作/特效参考图，只用于动作调度、速度感、空间关系和特效路径"]
    : [];
  const refs = [
    ...firstRefText,
    ...selectedRefs.map((ref, index) => `@图片${index + 1 + firstRefText.length} 是${ref.typeLabel}${ref.name ? `「${ref.name}」` : ""}`),
    ...localImages.map((ref, index) => `@图片${selectedRefs.length + index + 1 + firstRefText.length} 是上传参考图${ref.name ? `「${ref.name}」` : ""}`),
    ...localVideos.map((ref, index) => `@视频${index + 1} 是上传参考视频，只参考动作节奏、速度、镜头调度和特效发生顺序`),
  ].join("，");
  const ignoreNotes = annotationMode === "director"
    ? "如果参考图里有箭头、编号、timing、camera notes 或手写注解，只理解它们的动作方向和节奏含义，不要把任何文字、编号或标注生成到视频画面里。"
    : "参考图没有文字标注，只按动作顺序、特效路径和空间关系生成。";

  return [
    `${modeConfig.label}视频参考`,
    hasReferenceBoard
      ? `请参考这张黑白${styleConfig.label}，生成一段${modeConfig.label}视频。${refs}。`
      : `请直接根据招式/特效规则生成一段${modeConfig.label}视频。${refs || "无绑定参考素材，按文字描述执行"}。`,
    `固定招式/特效资产规则：${normalizeText(fixedRuleText) || "按参考图和核心内容执行。"}`,
    `核心内容：${normalizeText(promptText) || "动作/特效连续推进，爆点清楚，停在可接下一镜的位置。"}`,
    `${ignoreNotes}严格保留人物相对站位、动作路径、受力方向、特效轨迹、镜头节奏和爆点节拍。线稿只用于动作调度、速度感和空间关系；人物脸部以实际角色参考图为准。全程无字幕、无水印、禁止bgm，保留环境声和动作音效。`,
  ].join("\n");
};

const SegmentButton = ({ active, children, onClick, title }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className="cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors"
    style={{
      borderColor: active ? "var(--color-accent-primary)" : "var(--color-border-primary)",
      color: active ? "var(--color-accent-primary)" : "var(--color-text-secondary)",
      backgroundColor: active ? "var(--color-accent-light)" : "var(--color-bg-tertiary)",
    }}
  >
    {children}
  </button>
);

const readSavedPrompts = (projectId) => {
  try {
    const raw = localStorage.getItem(`fight_effect_saved_prompts_${projectId || "global"}`);
    const parsed = JSON.parse(raw || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const fightVideoPendingKey = (projectId) => `fight_effect_pending_video_${projectId || "global"}`;

const readPendingFightVideo = (projectId) => {
  try {
    const pending = JSON.parse(localStorage.getItem(fightVideoPendingKey(projectId)) || "null");
    return pending?.taskId && pending?.provider ? pending : null;
  } catch {
    return null;
  }
};

const savePendingFightVideo = (projectId, pending) => {
  try {
    localStorage.setItem(fightVideoPendingKey(projectId), JSON.stringify(pending));
  } catch {
    // localStorage 不可用时只失去刷新恢复能力，不影响当前生成任务。
  }
};

const clearPendingFightVideo = (projectId, taskId = "") => {
  try {
    const current = readPendingFightVideo(projectId);
    if (!taskId || !current || String(current.taskId) === String(taskId)) {
      localStorage.removeItem(fightVideoPendingKey(projectId));
    }
  } catch {
    // ignore
  }
};

const FightActionWorkshop = ({ projectId }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();

  const [assetType, setAssetType] = useState("combo");
  const [assetName, setAssetName] = useState(RULE_TEMPLATES.combo.assetName);
  const [ownerName, setOwnerName] = useState("");
  const [visualIdentity, setVisualIdentity] = useState(RULE_TEMPLATES.combo.visualIdentity);
  const [motionRule, setMotionRule] = useState(RULE_TEMPLATES.combo.motionRule);
  const [impactRule, setImpactRule] = useState(RULE_TEMPLATES.combo.impactRule);
  const [cameraRule, setCameraRule] = useState(RULE_TEMPLATES.combo.cameraRule);
  const [avoidRule, setAvoidRule] = useState(RULE_TEMPLATES.combo.avoidRule);
  const [promptText, setPromptText] = useState("一名修士在雨夜巷口结印，地面符文亮起，蓝白电弧沿墙面爬行，敌人冲近瞬间被冲击波掀飞。");
  const [mode, setMode] = useState("fight");
  const [referenceStyle, setReferenceStyle] = useState("3x3");
  const [annotationMode, setAnnotationMode] = useState("clean");
  const { imageProvider: modelId, imageQualityTier: effectiveImageQualityTier, availableImageQualityTierOptions, handleImageQualityTierChange } = useWorkshopImageSettings();
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [rightTab, setRightTab] = useState("assets");
  const [assets, setAssets] = useState([]);
  const [assetLoading, setAssetLoading] = useState(false);
  const [assetSearch, setAssetSearch] = useState("");
  const [selectedAssetKeys, setSelectedAssetKeys] = useState([]);
  const [localMediaRefs, setLocalMediaRefs] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [savedPrompts, setSavedPrompts] = useState(() => readSavedPrompts(projectId));
  const [currentImage, setCurrentImage] = useState(null);
  const [currentVideoPrompt, setCurrentVideoPrompt] = useState("");
  const [videoPromptDraft, setVideoPromptDraft] = useState("");
  const [projectRuntimeTick, setProjectRuntimeTick] = useState(0);
  const videoProvider = useMemo(() => getProjectVideoProvider(projectId), [projectId, projectRuntimeTick]);
  const overseasProject = isOverseasVideoProvider(videoProvider);
  const videoProviderLabel = getVideoProviderLabel(videoProvider);
  const videoModelOptions = useMemo(() => {
    if (videoProvider === VIDEO_PROVIDER_OMINI_SEEDANCE) return GUA_OMINI_VIDEO_MODEL_OPTIONS;
    return overseasProject ? OVERSEAS_VIDEO_MODEL_OPTIONS : DOMESTIC_VIDEO_MODEL_OPTIONS;
  }, [overseasProject, videoProvider]);
  const [videoModel, setVideoModel] = useState(() => getProjectDefaultVideoModel(projectId, true));
  const [videoResolution, setVideoResolution] = useState("480p");
  const [videoRatio, setVideoRatio] = useState(() => getProjectDefaultVideoRatio(projectId));
  const [videoDuration, setVideoDuration] = useState(5);
  const [videoGenerateAudio, setVideoGenerateAudio] = useState(true);
  const [videoGenerating, setVideoGenerating] = useState(false);
  const [videoProgressText, setVideoProgressText] = useState("");
  const [videoError, setVideoError] = useState("");
  const [currentVideo, setCurrentVideo] = useState(null);
  const [videoHistory, setVideoHistory] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const [progressText, setProgressText] = useState("");
  const [error, setError] = useState("");
  const videoPollStopRef = useRef(null);
  const videoPreflightRunRef = useRef(0);

  useEffect(() => {
    const onProfileChange = (event) => {
      if (!projectId || String(event?.detail?.projectId) === String(projectId)) {
        setProjectRuntimeTick((value) => value + 1);
      }
    };
    window.addEventListener("project-runtime-profile-change", onProfileChange);
    return () => window.removeEventListener("project-runtime-profile-change", onProfileChange);
  }, [projectId]);

  useEffect(() => {
    const allowed = new Set(videoModelOptions.map((item) => item.value));
    if (!allowed.has(videoModel)) {
      setVideoModel(getProjectDefaultVideoModel(projectId, true));
    }
  }, [projectId, videoModel, videoModelOptions]);

  useEffect(() => {
    setVideoRatio(getProjectDefaultVideoRatio(projectId));
  }, [projectId, projectRuntimeTick]);

  const selectedModel = useMemo(
    () => IMAGE_MODEL_OPTIONS.find(option => option.id === modelId) || { id: modelId, provider: modelId },
    [modelId]
  );

  const selectedRefs = useMemo(
    () => selectedAssetKeys.map(key => assets.find(asset => asset.key === key)).filter(Boolean),
    [assets, selectedAssetKeys]
  );

  const selectedMode = useMemo(
    () => EFFECT_MODE_OPTIONS.find(option => option.value === mode) || EFFECT_MODE_OPTIONS[0],
    [mode]
  );

  const selectedAssetType = useMemo(
    () => ASSET_TYPE_OPTIONS.find(option => option.value === assetType) || ASSET_TYPE_OPTIONS[2],
    [assetType]
  );

  const selectedStyle = useMemo(
    () => REFERENCE_STYLE_OPTIONS.find(option => option.value === referenceStyle) || REFERENCE_STYLE_OPTIONS[0],
    [referenceStyle]
  );

  const fixedRuleText = useMemo(
    () => buildFixedRuleText({
      assetType,
      assetName,
      ownerName,
      visualIdentity,
      motionRule,
      impactRule,
      cameraRule,
      avoidRule,
    }),
    [assetType, assetName, ownerName, visualIdentity, motionRule, impactRule, cameraRule, avoidRule]
  );

  const generatedPrompt = useMemo(
    () => buildReferencePrompt({ promptText, fixedRuleText, mode, referenceStyle, annotationMode, selectedRefs, localMediaRefs }),
    [promptText, fixedRuleText, mode, referenceStyle, annotationMode, selectedRefs, localMediaRefs]
  );

  const generatedVideoPrompt = useMemo(
    () => buildVideoPrompt({ promptText, fixedRuleText, mode, referenceStyle, annotationMode, selectedRefs, localMediaRefs, hasReferenceBoard: Boolean(currentImage) }),
    [promptText, fixedRuleText, mode, referenceStyle, annotationMode, selectedRefs, localMediaRefs, currentImage]
  );

  const filteredAssets = useMemo(() => {
    const kw = assetSearch.trim().toLowerCase();
    if (!kw) return assets;
    return assets.filter(asset => {
      const haystack = `${asset.name || ""} ${asset.parentName || ""} ${asset.typeLabel || ""} ${asset.description || ""}`.toLowerCase();
      return haystack.includes(kw);
    });
  }, [assets, assetSearch]);

  const loadAssets = useCallback(async () => {
    if (!projectId) return;
    setAssetLoading(true);
    try {
      const [characters, scenes, props] = await Promise.all([
        authenticatedFetch(`${API_HOST}/api/characters?projectId=${projectId}`).catch(() => []),
        authenticatedFetch(`${API_HOST}/api/scenes?projectId=${projectId}`).catch(() => []),
        authenticatedFetch(`${API_HOST}/api/props?projectId=${projectId}`).catch(() => []),
      ]);
      setAssets(buildAssetOptions(characters || [], scenes || [], props || []));
    } finally {
      setAssetLoading(false);
    }
  }, [projectId]);

  const loadHistory = useCallback(async () => {
    if (!projectId) return;
    setHistoryLoading(true);
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/resource/query`, {
        method: "POST",
        body: JSON.stringify({
          projectId: Number(projectId),
          types: ["AGENT_IMAGE"],
          includeMetadata: true,
        }),
      });
      const items = Array.isArray(data) ? data : [];
      const fightItems = items
        .map(item => ({ ...item, parsedMetadata: parseMetadata(item.metadata) }))
        .filter(item => {
          const metadata = item.parsedMetadata || {};
          return metadata.origin === "fight_action_workshop"
            || metadata.origin === "fight_effect_workshop"
            || metadata.generationMode === "fight_action_3x3"
            || metadata.generationMode === "fight_action_board"
            || metadata.generationMode === "effect_reference_board";
        })
        .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
      setHistory(fightItems);
      setCurrentImage(prev => prev || (isDreaminaProxySeedance20Provider(videoProvider)
        ? getDreaminaProxyOriginalUrl(fightItems[0]) || fightItems[0]?.url : fightItems[0]?.url) || null);
      setCurrentVideoPrompt(prev => prev || fightItems[0]?.parsedMetadata?.videoPrompt || "");
    } catch (err) {
      console.error("[FightActionWorkshop] 加载历史失败:", err);
    } finally {
      setHistoryLoading(false);
    }
  }, [projectId, videoProvider]);

  useEffect(() => {
    loadAssets();
    loadHistory();
  }, [loadAssets, loadHistory]);

  useEffect(() => {
    setSavedPrompts(readSavedPrompts(projectId));
  }, [projectId]);

  useEffect(() => {
    setAspectRatio(selectedStyle.aspectRatio);
  }, [selectedStyle.aspectRatio]);

  useEffect(() => {
    setVideoPromptDraft(currentVideoPrompt || generatedVideoPrompt);
  }, [currentVideoPrompt, generatedVideoPrompt]);

  useEffect(() => {
    setCurrentVideo(prev => {
      if (!prev?.referenceImage || !currentImage || prev.referenceImage === currentImage) return prev;
      return null;
    });
  }, [currentImage]);

  useEffect(() => () => {
    videoPreflightRunRef.current += 1;
    if (videoPollStopRef.current) {
      videoPollStopRef.current();
      videoPollStopRef.current = null;
    }
  }, []);

  useEffect(() => {
    const pending = readPendingFightVideo(projectId);
    if (!pending || videoPollStopRef.current) return undefined;
    const taskId = pending.taskId;
    const restoredVideo = {
      ...(pending.video || {}),
      id: taskId,
      taskId,
      status: "processing",
      progress: Number(pending.video?.progress) || 8,
      // 切换当前参考板不应把一个已提交的恢复任务从界面清掉。
      referenceImage: "",
    };
    setCurrentVideo(restoredVideo);
    setVideoGenerating(true);
    setVideoError("");
    setVideoProgressText("正在恢复视频任务查询");
    videoPollStopRef.current = startPolling(pending.provider, taskId, {
      projectId: projectId ? Number(projectId) : undefined,
      model: pending.model,
      subModel: pending.model,
      duration: pending.duration,
      mode: pending.mode,
      sound: pending.sound,
      resolution: pending.resolution,
      hasReferenceVideo: pending.hasReferenceVideo,
      onProgress: (status) => {
        setVideoProgressText(status?.status === "queued" ? "排队中" : "视频生成中");
        setCurrentVideo((previous) => previous
          ? { ...previous, status: "processing", progress: status?.progress || previous.progress || 12 }
          : previous);
      },
      onSuccess: (data) => {
        const videoUrl = extractVideoUrl(data);
        const nextVideo = {
          ...restoredVideo,
          status: "success",
          progress: 100,
          videoUrl,
          completedAt: new Date().toISOString(),
        };
        clearPendingFightVideo(projectId, taskId);
        setCurrentVideo(nextVideo);
        setVideoHistory((previous) => [nextVideo, ...previous.filter((item) => item.taskId !== taskId)].slice(0, 30));
        setVideoGenerating(false);
        setVideoProgressText("");
        videoPollStopRef.current = null;
        toast.success("视频生成完成");
      },
      onError: (errorMsg) => {
        const message = errorMsg || "视频生成失败";
        clearPendingFightVideo(projectId, taskId);
        setVideoError(message);
        setVideoGenerating(false);
        setVideoProgressText("");
        setCurrentVideo((previous) => previous ? { ...previous, status: "failed", error: message } : previous);
        videoPollStopRef.current = null;
        toast.error(`视频生成失败: ${message}`, 5000);
      },
    });
    return undefined;
  }, [projectId, toast]);

  const persistSavedPrompts = (items) => {
    setSavedPrompts(items);
    localStorage.setItem(`fight_effect_saved_prompts_${projectId || "global"}`, JSON.stringify(items));
  };

  const toggleAsset = (assetKey) => {
    setSelectedAssetKeys(prev => {
      if (prev.includes(assetKey)) return prev.filter(key => key !== assetKey);
      if (prev.length >= 6) {
        toast.warning("参考图最多选择 6 张");
        return prev;
      }
      const next = [...prev, assetKey];
      return next;
    });
  };

  const handleUploadLocalMedia = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    if (localMediaRefs.length + files.length > 8) {
      toast.warning("本地参考素材最多 8 个");
      event.target.value = "";
      return;
    }
    setUploadingMedia(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const kind = file.type.startsWith("video/") ? "video" : "image";
        if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
          toast.warning(`${file.name} 不是图片或视频，已跳过`);
          continue;
        }
        const formData = new FormData();
        formData.append("file", file);
        const url = await authenticatedFetch(`${API_HOST}/api/upload/asset`, { method: "POST", body: formData });
        uploaded.push({
          id: `local-${Date.now()}-${uploaded.length}`,
          kind,
          name: file.name,
          url,
        });
      }
      if (uploaded.length) {
        setLocalMediaRefs(prev => [...prev, ...uploaded]);
        toast.success(`已添加 ${uploaded.length} 个参考素材`);
      }
    } catch (err) {
      toast.error(`上传失败: ${err.message || "未知错误"}`, 5000);
    } finally {
      setUploadingMedia(false);
      event.target.value = "";
    }
  };

  const removeLocalMediaRef = (id) => {
    setLocalMediaRefs(prev => prev.filter(item => item.id !== id));
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label}已复制`);
    } catch {
      toast.error("复制失败");
    }
  };

  const downloadImage = () => {
    if (!currentImage) return;
    const anchor = document.createElement("a");
    anchor.href = currentImage;
    anchor.download = `${safeFileName(assetName || promptText)}-${referenceStyle}.png`;
    anchor.target = "_blank";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const applyRuleTemplate = (nextType = assetType) => {
    const template = RULE_TEMPLATES[nextType] || RULE_TEMPLATES.combo;
    setAssetType(nextType);
    setAssetName(template.assetName);
    setVisualIdentity(template.visualIdentity);
    setMotionRule(template.motionRule);
    setImpactRule(template.impactRule);
    setCameraRule(template.cameraRule);
    setAvoidRule(template.avoidRule);
  };

  const handleSavePrompt = () => {
    if (!normalizeText(promptText)) {
      toast.warning("先填写提示词");
      return;
    }
    const item = {
      id: `fx-${Date.now()}`,
      title: normalizeText(assetName) || summarizePromptTitle(promptText, selectedMode.label),
      assetType,
      assetTypeLabel: selectedAssetType.label,
      assetName,
      ownerName,
      visualIdentity,
      motionRule,
      impactRule,
      cameraRule,
      avoidRule,
      fixedRuleText,
      prompt: promptText,
      mode,
      referenceStyle,
      annotationMode,
      createdAt: new Date().toISOString(),
    };
    persistSavedPrompts([item, ...savedPrompts].slice(0, 60));
    setRightTab("saved");
    toast.success("招式/特效规则已保存");
  };

  const handleRemoveSavedPrompt = (id) => {
    persistSavedPrompts(savedPrompts.filter(item => item.id !== id));
  };

  const handleUseSavedPrompt = (item) => {
    if (item.assetType) setAssetType(item.assetType);
    if (item.assetName) setAssetName(item.assetName);
    if (item.ownerName !== undefined) setOwnerName(item.ownerName || "");
    if (item.visualIdentity) setVisualIdentity(item.visualIdentity);
    if (item.motionRule) setMotionRule(item.motionRule);
    if (item.impactRule) setImpactRule(item.impactRule);
    if (item.cameraRule) setCameraRule(item.cameraRule);
    if (item.avoidRule) setAvoidRule(item.avoidRule);
    setPromptText(item.prompt || "");
    if (item.mode) setMode(item.mode);
    if (item.referenceStyle) setReferenceStyle(item.referenceStyle);
    if (item.annotationMode) setAnnotationMode(item.annotationMode);
  };

  const applyWorkshopMetadata = (metadata = {}) => {
    if (metadata.assetType) setAssetType(metadata.assetType);
    if (metadata.assetName) setAssetName(metadata.assetName);
    if (metadata.ownerName !== undefined) setOwnerName(metadata.ownerName || "");
    if (metadata.visualIdentity) setVisualIdentity(metadata.visualIdentity);
    if (metadata.motionRule) setMotionRule(metadata.motionRule);
    if (metadata.impactRule) setImpactRule(metadata.impactRule);
    if (metadata.cameraRule) setCameraRule(metadata.cameraRule);
    if (metadata.avoidRule) setAvoidRule(metadata.avoidRule);
    if (metadata.promptText) setPromptText(metadata.promptText);
    if (metadata.mode) setMode(metadata.mode);
    if (metadata.referenceStyle) setReferenceStyle(metadata.referenceStyle);
    if (metadata.annotationMode) setAnnotationMode(metadata.annotationMode);
  };

  const patchMetadata = async (imageUrl, prompt, videoPrompt) => {
    if (!imageUrl || !projectId) return;
    const metadata = {
      origin: "fight_effect_workshop",
      generationMode: "effect_reference_board",
      assetType,
      assetTypeLabel: selectedAssetType.label,
      assetName,
      ownerName,
      fixedRuleText,
      visualIdentity,
      motionRule,
      impactRule,
      cameraRule,
      avoidRule,
      promptText,
      mode,
      modeLabel: selectedMode.label,
      referenceStyle,
      referenceStyleLabel: selectedStyle.label,
      annotationMode,
      modelId: selectedModel.id,
      provider: selectedModel.provider,
      ...(selectedModel.imageModel ? { imageModel: selectedModel.imageModel } : {}),
      aspectRatio,
      prompt,
      videoPrompt,
      referenceImages: selectedRefs.map((ref, index) => ({
        index: index + 1,
        name: ref.name,
        parentName: ref.parentName || "",
        type: ref.typeLabel,
        url: ref.url,
      })),
      localMediaRefs,
    };
    await authenticatedFetch(`${API_HOST}/api/resource/metadata`, {
      method: "PATCH",
      body: JSON.stringify({
        projectId: Number(projectId),
        resourceUrl: imageUrl,
        metadata: JSON.stringify(metadata),
      }),
    }).catch(err => console.warn("[FightActionWorkshop] PATCH metadata 失败:", err.message));
  };

  const patchVideoMetadata = async (videoUrl, metadata) => {
    if (!videoUrl || !projectId) return;
    await authenticatedFetch(`${API_HOST}/api/resource/metadata`, {
      method: "PATCH",
      body: JSON.stringify({
        projectId: Number(projectId),
        videoUrl,
        resourceUrl: videoUrl,
        metadata: JSON.stringify(metadata),
      }),
    }).catch(err => console.warn("[FightActionWorkshop] PATCH video metadata 失败:", err.message));
  };

  const buildGuaVideoContents = (prompt) => {
    const contents = [];
    if (prompt) contents.push({ type: "text", text: prompt });
    if (currentImage) {
      contents.push({ type: "image_url", url: currentImage, role: "reference_image" });
    }
    selectedRefs.forEach((ref) => {
      if (!ref?.url && !ref?.assetId && !ref?.engGuaVideoAssetId && !ref?.guaOminiAssetId && !ref?.dreaminaProxyAssetId) return;
      const content = { type: "image_url", url: isDreaminaProxySeedance20Provider(videoProvider)
        ? requireDreaminaProxyOriginalUrl(ref) : ref.url, role: "reference_image" };
      const assetMeta = getEffectiveAssetMeta(ref, videoProvider);
      const assetId = String(assetMeta.assetId || "").replace(/^asset:\/\//i, "");
      if (assetId && String(assetMeta.status || "").toLowerCase() === "active" && videoProvider === "guavideo") {
        content.assetId = assetId;
      }
      contents.push(content);
    });
    localMediaRefs.forEach((ref) => {
      if (!ref?.url && !ref?.assetId && !ref?.engGuaVideoAssetId && !ref?.guaOminiAssetId && !ref?.dreaminaProxyAssetId) return;
      contents.push({
        type: ref.kind === "video" ? "video_url" : "image_url",
        url: isDreaminaProxySeedance20Provider(videoProvider) ? requireDreaminaProxyOriginalUrl(ref) : ref.url,
        role: ref.kind === "video" ? "reference_video" : "reference_image",
      });
    });
    return contents;
  };

  const handleGenerateVideo = async () => {
    if (!isSeedanceVideoProvider(videoProvider)) {
      setVideoError("项目视频厂商配置尚未加载，请刷新项目后重试");
      return;
    }
    const prompt = normalizeText(videoPromptDraft) || currentVideoPrompt || generatedVideoPrompt;
    if (!prompt) {
      setVideoError("请先填写视频提示词");
      return;
    }
    let requestedContents;
    if (videoPollStopRef.current) {
      videoPollStopRef.current();
      videoPollStopRef.current = null;
    }

    const createdAt = new Date().toISOString();
    const preflightRun = videoPreflightRunRef.current + 1;
    videoPreflightRunRef.current = preflightRun;
    const localId = `fx-video-${Date.now()}`;
    setVideoGenerating(true);
    setVideoProgressText(`提交 ${videoProviderLabel} 任务中`);
    setVideoError("");
    setCurrentVideo({
      id: localId,
      status: "processing",
      prompt,
      videoUrl: "",
      createdAt,
    });

    try {
      requestedContents = buildGuaVideoContents(prompt);
      const hasVideoReferences = Boolean(currentImage || selectedRefs.length || localMediaRefs.length);
      setVideoProgressText(`提交 ${videoProviderLabel} 任务中`);
      const preparedContents = await preflightVideoAssetContents({
        provider: videoProvider,
        projectId,
        contents: requestedContents,
        context: {
          name: normalizeText(assetName) || "打戏动作参考图",
          source: "fight_effect_workshop",
          sourceDetail: "fight_effect_video_preflight",
          typeCode: "agent_image",
        },
        shouldContinue: () => videoPreflightRunRef.current === preflightRun,
      });
      const payload = {
        provider: videoProvider,
        model: videoModel,
        prompt,
        duration: videoDuration,
        aspectRatio: videoRatio,
        ratio: videoRatio,
        resolution: videoResolution,
        projectId: projectId ? Number(projectId) : undefined,
        guaVideoMode: hasVideoReferences ? "ref2v" : "t2v",
        generateAudio: videoGenerateAudio,
        guaVideoContents: preparedContents,
      };

      const response = await submitVideoTask(payload);
      const responseProvider = normalizeVideoProvider(response?.provider || response?.videoProvider);
      if (responseProvider && responseProvider !== videoProvider) {
        throw new Error("视频服务返回与当前项目不匹配，已拒绝接收任务");
      }
      const taskId = response?.taskId;
      if (!taskId) throw new Error("后端未返回视频任务ID");

      setCurrentVideo(prev => ({
        ...(prev || {}),
        id: taskId,
        taskId,
        status: "processing",
        progress: 8,
      }));
      setVideoProgressText(`${videoProviderLabel} 已提交，等待生成结果`);

      savePendingFightVideo(projectId, {
        taskId,
        provider: videoProvider,
        model: videoModel,
        duration: videoDuration,
        mode: hasVideoReferences ? "ref2v" : "t2v",
        sound: videoGenerateAudio ? "on" : "off",
        resolution: videoResolution,
        hasReferenceVideo: hasVideoReferences,
        video: {
          id: taskId,
          taskId,
          status: "processing",
          progress: 8,
          prompt,
          model: videoModel,
          resolution: videoResolution,
          ratio: videoRatio,
          duration: videoDuration,
          createdAt,
        },
      });

      videoPollStopRef.current = startPolling(videoProvider, taskId, {
        projectId: projectId ? Number(projectId) : undefined,
        prompt,
        model: videoModel,
        subModel: videoModel,
        duration: videoDuration,
        mode: hasVideoReferences ? "ref2v" : "t2v",
        sound: videoGenerateAudio ? "on" : "off",
        resolution: videoResolution,
        hasReferenceVideo: hasVideoReferences,
        onProgress: (status) => {
          setVideoProgressText(status?.status === "queued" ? "排队中" : "视频生成中");
          setCurrentVideo(prev => prev ? { ...prev, status: "processing", progress: status?.progress || prev.progress || 12 } : prev);
        },
        onSuccess: async (data) => {
          const videoUrl = extractVideoUrl(data);
          const nextVideo = {
            id: taskId,
            taskId,
            status: "success",
            progress: 100,
            videoUrl,
            prompt,
            model: videoModel,
            resolution: videoResolution,
            ratio: videoRatio,
            duration: videoDuration,
            referenceImage: currentImage,
            referenceMedia: localMediaRefs,
            createdAt,
            completedAt: new Date().toISOString(),
          };
          setCurrentVideo(nextVideo);
          setVideoHistory(prev => [nextVideo, ...prev.filter(item => item.taskId !== taskId)].slice(0, 30));
          setVideoGenerating(false);
          setVideoProgressText("");
          videoPollStopRef.current = null;
          clearPendingFightVideo(projectId, taskId);
          if (videoUrl) {
            await patchVideoMetadata(videoUrl, {
              origin: "fight_effect_workshop",
              generationMode: "effect_reference_video",
              assetType,
              assetTypeLabel: selectedAssetType.label,
              assetName,
              ownerName,
              fixedRuleText,
              prompt,
              referenceImage: currentImage,
              referenceMedia: localMediaRefs,
              mode,
              modeLabel: selectedMode.label,
              referenceStyle,
              referenceStyleLabel: selectedStyle.label,
              model: videoModel,
              resolution: videoResolution,
              ratio: videoRatio,
              duration: videoDuration,
              generateAudio: videoGenerateAudio,
              referenceImages: selectedRefs.map((ref, index) => ({
                index: index + 2,
                name: ref.name,
                parentName: ref.parentName || "",
                type: ref.typeLabel,
                url: ref.url,
                guaVideoAssetId: ref.guaVideoAssetId || "",
                guaVideoAssetStatus: ref.guaVideoAssetStatus || "",
                engGuaVideoAssetId: ref.engGuaVideoAssetId || "",
                engGuaVideoAssetStatus: ref.engGuaVideoAssetStatus || "",
                guaOminiAssetId: ref.guaOminiAssetId || "",
                guaOminiAssetStatus: ref.guaOminiAssetStatus || "",
                dreaminaProxyAssetId: ref.dreaminaProxyAssetId || "",
                dreaminaProxyAssetStatus: ref.dreaminaProxyAssetStatus || "",
              })),
            });
          }
          toast.success("视频生成完成");
        },
        onError: (errorMsg) => {
          const message = errorMsg || "视频生成失败";
          setVideoError(message);
          setVideoGenerating(false);
          setVideoProgressText("");
          setCurrentVideo(prev => prev ? { ...prev, status: "failed", error: message } : prev);
          videoPollStopRef.current = null;
          clearPendingFightVideo(projectId, taskId);
          toast.error(`视频生成失败: ${message}`, 5000);
        },
      });
    } catch (err) {
      const message = err.message || "视频生成失败";
      setVideoError(message);
      setVideoGenerating(false);
      setVideoProgressText("");
      setCurrentVideo(prev => prev ? { ...prev, status: "failed", error: message } : prev);
      toast.error(`视频生成失败: ${message}`, 5000);
    }
  };

  const handleExpandPrompt = async () => {
    if (!normalizeText(promptText)) {
      setError("请先填写提示词");
      return;
    }
    setIsExpanding(true);
    setProgressText("AI 正在扩写提示词");
    setError("");
    try {
      const { result } = await runTaskToCompletion("FIGHT_EFFECT_PROMPT_EXPAND", {
        projectId: projectId ? Number(projectId) : null,
        prompt: promptText,
        assetType,
        assetTypeLabel: selectedAssetType.label,
        assetName,
        ownerName,
        fixedRuleText,
        mode,
        modeLabel: selectedMode.label,
        referenceStyle,
        referenceStyleLabel: selectedStyle.label,
        annotationMode,
      }, {
        projectId: projectId ? Number(projectId) : undefined,
        provider: isDatastartImageProvider(selectedModel.provider)
          ? resolveDatastartWorkflowTextProvider(localStorage.getItem('ai_provider'))
          : isAstraflowImageProvider(selectedModel.provider)
            ? normalizeTextProvider(localStorage.getItem('ai_provider')) : selectedModel.provider,
        source: "fight_effect_prompt_expand",
        timeoutMs: 10 * 60 * 1000,
        onProgress: status => setProgressText(status.currentStageText || status.status || "扩写中"),
      });
      const expanded = extractExpandedPrompt(result);
      if (!expanded) throw new Error("扩写完成但没有返回提示词");
      setPromptText(expanded);
      toast.success("提示词已扩写");
    } catch (err) {
      setError(err.message || "扩写失败");
      toast.error(`扩写失败: ${err.message || "未知错误"}`, 5000);
    } finally {
      setIsExpanding(false);
      setProgressText("");
    }
  };

  const handleGenerate = async () => {
    if (!normalizeText(promptText)) {
      setError("请先填写提示词");
      return;
    }
    setIsGenerating(true);
    setProgressText("任务提交中");
    setError("");
    const prompt = generatedPrompt;
    const videoPrompt = generatedVideoPrompt;
    try {
      const referenceImages = [
        ...selectedRefs.map((ref, index) => ({
          name: `${ref.typeLabel}${index + 1}-${ref.name}`,
          base64: ref.url,
        })),
        ...localMediaRefs
          .filter(ref => ref.kind === "image")
          .map((ref, index) => ({
            name: `上传参考图${index + 1}-${ref.name || "image"}`,
            base64: ref.url,
          })),
      ];
      assertWanxImageInputLimit(
        referenceImages.map(reference => reference.base64).filter(Boolean),
        selectedModel.provider
      );
      const { result, url } = await runTaskToCompletion("SIMPLE_GENERATE_MULTIPLE", {
        provider: selectedModel.provider,
        ...(selectedModel.imageModel ? { imageModel: selectedModel.imageModel } : {}),
        prompt,
        aspectRatio: normalizeDatastartImageAspectRatio(aspectRatio, selectedModel.provider),
        imageQualityTier: effectiveImageQualityTier,
        count: 1,
        projectId: projectId ? Number(projectId) : null,
        referenceImages,
      }, {
        projectId: projectId ? Number(projectId) : undefined,
        provider: selectedModel.provider,
        source: "fight_effect_workshop",
        timeoutMs: 30 * 60 * 1000,
        onProgress: status => setProgressText(status.currentStageText || status.status || "生成中"),
      });

      const imageUrl = extractGeneratedImageUrl(result, url);
      if (!imageUrl) throw new Error("任务完成但没有返回图片地址");
      setCurrentImage(imageUrl);
      setCurrentVideoPrompt(videoPrompt);
      await patchMetadata(imageUrl, prompt, videoPrompt);
      await loadHistory();
      toast.success("参考图生成完成");
    } catch (err) {
      setError(err.message || "生成失败");
      toast.error(`生成失败: ${err.message || "未知错误"}`, 5000);
    } finally {
      setIsGenerating(false);
      setProgressText("");
    }
  };

  const selectedHistory = history.find(item => item.url === currentImage);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden" style={{ backgroundColor: "var(--color-bg-primary)", color: "var(--color-text-primary)" }}>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-lg border"
            style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}
          >
            <Swords size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold">招式/特效工坊</h1>
            <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>固定招式、法术、爆点和动作路径，生成黑白分镜参考图并继续生成视频。</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AppSelect
            value={effectiveImageQualityTier}
            onChange={e => handleImageQualityTierChange(e.target.value)}
            title="图片清晰度档位"
            className="h-9 rounded-full border px-3 text-sm outline-none"
            style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}
          >
            {availableImageQualityTierOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </AppSelect>
          <AppSelect
            value={normalizeDatastartImageAspectRatio(aspectRatio, selectedModel.provider)}
            onChange={e => setAspectRatio(e.target.value)}
            className="h-9 rounded-full border px-3 text-sm outline-none"
            style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}
          >
            {getCompatibleImageAspectOptions(['16:9', '1:1', '9:16'].map(value => ({ value, label: value })), selectedModel.provider)
              .map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </AppSelect>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating || isExpanding}
            className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-full px-4 text-sm font-bold text-[var(--color-accent-contrast)] disabled:opacity-60"
            style={{ backgroundColor: theme.colors.accentPrimary }}
          >
            {isGenerating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            生成参考图
          </button>
        </div>
      </header>

      <main className="grid min-h-0 flex-1 grid-cols-[420px_minmax(0,1fr)_340px] gap-3 overflow-hidden p-3 max-[1450px]:grid-cols-[360px_minmax(0,1fr)_310px]">
        <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
          <div className="border-b px-4 py-3" style={{ borderColor: "var(--color-border-primary)" }}>
            <h2 className="text-sm font-bold">固定资产规则</h2>
            <p className="mt-1 text-xs" style={{ color: "var(--color-text-secondary)" }}>先固定招式/特效识别点，再生成参考图和视频。</p>
          </div>
          <div className="custom-scrollbar flex-1 space-y-4 overflow-y-auto p-4">
            <div className="space-y-2">
              <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>资产类型</span>
              <div className="grid grid-cols-3 gap-2">
                {ASSET_TYPE_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setAssetType(option.value);
                      if (!normalizeText(assetName) || assetName === RULE_TEMPLATES[assetType]?.assetName) {
                        applyRuleTemplate(option.value);
                      }
                    }}
                    className="cursor-pointer rounded-lg border px-3 py-2 text-left transition-colors"
                    title={option.description}
                    style={{
                      borderColor: assetType === option.value ? theme.colors.accentPrimary : "var(--color-border-primary)",
                      backgroundColor: assetType === option.value ? "var(--color-accent-light)" : "var(--color-bg-tertiary)",
                    }}
                  >
                    <div className="text-sm font-bold">{option.label}</div>
                    <div className="mt-1 line-clamp-2 text-2xs leading-4" style={{ color: "var(--color-text-secondary)" }}>{option.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-lg border p-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}>
              <div className="mb-3 flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-bold">稳定规则</div>
                  <div className="text-2xs" style={{ color: "var(--color-text-secondary)" }}>这些内容会进入生图和视频提示词。</div>
                </div>
                <button
                  type="button"
                  onClick={() => applyRuleTemplate(assetType)}
                  className="h-8 cursor-pointer rounded-full border px-3 text-xs font-bold"
                  style={{ borderColor: "var(--color-border-primary)", color: theme.colors.accentPrimary }}
                >
                  套用模板
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <label className="space-y-1">
                  <span className="text-2xs font-bold" style={{ color: "var(--color-text-secondary)" }}>名称</span>
                  <input
                    value={assetName}
                    onChange={e => setAssetName(e.target.value)}
                    placeholder="例如 风雷破阵斩"
                    className="h-9 w-full rounded-lg border px-3 text-sm outline-none"
                    style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-2xs font-bold" style={{ color: "var(--color-text-secondary)" }}>归属/使用者</span>
                  <input
                    value={ownerName}
                    onChange={e => setOwnerName(e.target.value)}
                    placeholder="可空，例如 小春"
                    className="h-9 w-full rounded-lg border px-3 text-sm outline-none"
                    style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}
                  />
                </label>
              </div>

              <label className="mt-3 block space-y-1">
                <span className="text-2xs font-bold" style={{ color: "var(--color-text-secondary)" }}>稳定视觉识别</span>
                <textarea
                  value={visualIdentity}
                  onChange={e => setVisualIdentity(e.target.value)}
                  rows={2}
                  className="w-full resize-none rounded-lg border px-3 py-2 text-xs leading-5 outline-none"
                  style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}
                />
              </label>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <label className="space-y-1">
                  <span className="text-2xs font-bold" style={{ color: "var(--color-text-secondary)" }}>动作/发生规则</span>
                  <textarea
                    value={motionRule}
                    onChange={e => setMotionRule(e.target.value)}
                    rows={4}
                    className="w-full resize-none rounded-lg border px-3 py-2 text-xs leading-5 outline-none"
                    style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-2xs font-bold" style={{ color: "var(--color-text-secondary)" }}>命中/爆点规则</span>
                  <textarea
                    value={impactRule}
                    onChange={e => setImpactRule(e.target.value)}
                    rows={4}
                    className="w-full resize-none rounded-lg border px-3 py-2 text-xs leading-5 outline-none"
                    style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}
                  />
                </label>
              </div>

              <label className="mt-3 block space-y-1">
                <span className="text-2xs font-bold" style={{ color: "var(--color-text-secondary)" }}>镜头规则</span>
                <input
                  value={cameraRule}
                  onChange={e => setCameraRule(e.target.value)}
                  className="h-9 w-full rounded-lg border px-3 text-xs outline-none"
                  style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}
                />
              </label>

              <label className="mt-3 block space-y-1">
                <span className="text-2xs font-bold" style={{ color: "var(--color-text-secondary)" }}>禁忌规则</span>
                <input
                  value={avoidRule}
                  onChange={e => setAvoidRule(e.target.value)}
                  className="h-9 w-full rounded-lg border px-3 text-xs outline-none"
                  style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}
                />
              </label>
            </div>

            <div className="space-y-2">
              <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>模式</span>
              <div className="grid grid-cols-2 gap-2">
                {EFFECT_MODE_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setMode(option.value)}
                    className="cursor-pointer rounded-lg border p-3 text-left transition-colors"
                    style={{
                      borderColor: mode === option.value ? theme.colors.accentPrimary : "var(--color-border-primary)",
                      backgroundColor: mode === option.value ? "var(--color-accent-light)" : "var(--color-bg-tertiary)",
                    }}
                  >
                    <div className="text-sm font-bold">{option.label}</div>
                    <div className="mt-1 line-clamp-2 text-2xs leading-4" style={{ color: "var(--color-text-secondary)" }}>{option.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <label className="block space-y-2">
              <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>本次画面描述</span>
              <textarea
                value={promptText}
                onChange={e => setPromptText(e.target.value)}
                rows={7}
                placeholder="例：女主在雨夜天台挥剑，剑气切开雨幕，敌人被蓝白冲击波逼退，镜头从低机位推近到爆点。"
                className="w-full resize-none rounded-lg border px-3 py-3 text-sm leading-6 outline-none"
                style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}
              />
            </label>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>上传参考图片/视频</span>
                <label
                  className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-xs font-bold"
                  style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)", color: theme.colors.accentPrimary }}
                >
                  {uploadingMedia ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  添加
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/mp4,video/quicktime,video/*"
                    onChange={handleUploadLocalMedia}
                    className="hidden"
                    disabled={uploadingMedia}
                  />
                </label>
              </div>
              <div className="rounded-lg border p-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}>
                {localMediaRefs.length ? (
                  <div className="grid grid-cols-2 gap-2">
                    {localMediaRefs.map(ref => (
                      <div key={ref.id} className="group relative overflow-hidden rounded-lg border" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
                        <div className="aspect-video overflow-hidden">
                          {ref.kind === "video" ? (
                            <video src={ref.url} className="h-full w-full object-cover" muted playsInline />
                          ) : (
                            <img src={ref.url} alt={ref.name || "参考图"} className="h-full w-full object-cover" />
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-1 px-2 py-1.5">
                          <span className="min-w-0 truncate text-2xs" style={{ color: "var(--color-text-secondary)" }}>{ref.kind === "video" ? "视频" : "图片"} · {ref.name}</span>
                          <button type="button" onClick={() => removeLocalMediaRef(ref.id)} className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border" style={{ borderColor: "var(--color-border-primary)" }}>
                            <X size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="px-2 py-3 text-xs leading-5" style={{ color: "var(--color-text-secondary)" }}>
                    可上传图片参与参考图生成；上传视频会参与招式视频生成，用于控制动作节奏、镜头和特效路径。
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>参考形式</span>
              <div className="flex flex-wrap gap-2">
                {REFERENCE_STYLE_OPTIONS.map(option => (
                  <SegmentButton
                    key={option.value}
                    active={referenceStyle === option.value}
                    onClick={() => setReferenceStyle(option.value)}
                    title={option.description}
                  >
                    {option.label}
                  </SegmentButton>
                ))}
              </div>
              <p className="text-2xs leading-5" style={{ color: "var(--color-text-tertiary)" }}>{selectedStyle.description}</p>
            </div>

            <div className="space-y-2">
              <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>标注</span>
              <div className="flex flex-wrap gap-2">
                {ANNOTATION_MODE_OPTIONS.map(option => (
                  <SegmentButton
                    key={option.value}
                    active={annotationMode === option.value}
                    onClick={() => setAnnotationMode(option.value)}
                    title={option.description}
                  >
                    {option.label}
                  </SegmentButton>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={handleExpandPrompt}
                title={isDatastartImageProvider(selectedModel.provider)
                  ? 'DataStart 暂未提供文字模型，后续补充；扩写仅使用您另行选择的文字模型，不自动切换线路'
                  : isAstraflowImageProvider(selectedModel.provider) ? '所选线路仅生图；扩写使用当前已选择的文字模型' : '扩写提示词'}
                disabled={isExpanding || isGenerating}
                className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-lg border text-sm font-bold disabled:opacity-60"
                style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}
              >
                {isExpanding ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />}
                AI扩写
              </button>
              <button
                type="button"
                onClick={handleSavePrompt}
                className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-lg border text-sm font-bold"
                style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}
              >
                <Check size={15} />
                保存规则
              </button>
              <button
                type="button"
                onClick={() => copyText(generatedPrompt, "生图提示词")}
                className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-lg border text-sm font-bold"
                style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}
              >
                <Copy size={15} />
                复制
              </button>
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-lg border px-3 py-2 text-sm animate-in fade-in slide-in-from-top-1 duration-200" style={{ borderColor: "var(--color-red-primary)", color: "var(--color-red-primary)", backgroundColor: "var(--color-red-light)" }}>
                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </section>

        <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3" style={{ borderColor: "var(--color-border-primary)" }}>
            <div>
              <h2 className="text-sm font-bold">当前招式/特效参考</h2>
              <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                {getPublicVideoLabel(selectedHistory?.parsedMetadata?.provider || selectedModel.provider)} · {selectedAssetType.label} · {assetName || selectedMode.label} · {selectedStyle.label}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => copyText(currentVideoPrompt || generatedVideoPrompt, "视频提示词")}
                className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-xs font-semibold"
                style={{ borderColor: "var(--color-border-primary)" }}
              >
                <Zap size={14} /> 复制视频 Prompt
              </button>
              <button
                type="button"
                onClick={downloadImage}
                disabled={!currentImage}
                className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-xs font-semibold disabled:opacity-50"
                style={{ borderColor: "var(--color-border-primary)" }}
              >
                <Download size={14} /> 下载
              </button>
            </div>
          </div>

          <div className="custom-scrollbar flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
            <div className="relative flex min-h-[420px] flex-1 items-center justify-center overflow-hidden rounded-lg border" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
              {(isGenerating || isExpanding) && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 backdrop-blur-sm" style={{ backgroundColor: "var(--overlay-light)" }}>
                  <Loader2 className="animate-spin" size={30} style={{ color: theme.colors.accentPrimary }} />
                  <span className="text-sm font-bold">{progressText || "处理中"}</span>
                </div>
              )}
              {currentImage ? (
                <img src={currentImage} alt="动作特效参考图" className="h-full max-h-full w-full object-contain animate-in fade-in duration-300" />
              ) : (
                <div className="flex flex-col items-center gap-3 text-center" style={{ color: "var(--color-text-secondary)" }}>
                  <ImageIcon size={42} />
                  <div>
                    <p className="text-sm font-bold">还没有参考图</p>
                    <p className="mt-1 text-xs">输入一句动作/特效描述，选择模式后生成。</p>
                  </div>
                </div>
              )}
            </div>

            <div className="grid min-h-[132px] grid-cols-3 gap-3 max-[1500px]:grid-cols-2 max-[1300px]:grid-cols-1">
              <div className="rounded-lg border p-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>固定规则摘要</span>
                  <span className="rounded-full px-2 py-0.5 text-2xs font-bold" style={{ backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}>{selectedAssetType.label}</span>
                </div>
                <p className="line-clamp-5 whitespace-pre-line text-xs leading-5" style={{ color: "var(--color-text-secondary)" }}>{fixedRuleText}</p>
              </div>
              <div className="rounded-lg border p-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}>
                <div className="mb-2 text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>已选参考素材</div>
                {selectedRefs.length || localMediaRefs.length ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedRefs.map(ref => (
                      <button
                        key={ref.key}
                        type="button"
                        onClick={() => toggleAsset(ref.key)}
                        className="inline-flex max-w-full cursor-pointer items-center gap-2 rounded-full border px-2 py-1 text-xs"
                        style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}
                      >
                        <img src={ref.url} alt="" className="h-5 w-5 rounded-full object-cover" />
                        <span className="truncate">{ref.typeLabel}：{ref.name}</span>
                        <X size={12} />
                      </button>
                    ))}
                    {localMediaRefs.map(ref => (
                      <button
                        key={ref.id}
                        type="button"
                        onClick={() => removeLocalMediaRef(ref.id)}
                        className="inline-flex max-w-full cursor-pointer items-center gap-2 rounded-full border px-2 py-1 text-xs"
                        style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}
                      >
                        {ref.kind === "video" ? <Video size={14} /> : <ImageIcon size={14} />}
                        <span className="truncate">{ref.kind === "video" ? "参考视频" : "上传图片"}：{ref.name}</span>
                        <X size={12} />
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>可从右侧选择资产，或在左侧上传图片/视频作为参考。不是必选。</p>
                )}
              </div>
              <div className="rounded-lg border p-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>视频模型提示词</span>
                  <button type="button" onClick={() => copyText(currentVideoPrompt || generatedVideoPrompt, "视频提示词")} className="cursor-pointer text-xs" style={{ color: theme.colors.accentPrimary }}>复制</button>
                </div>
                <p className="line-clamp-4 text-xs leading-5" style={{ color: "var(--color-text-secondary)" }}>{currentVideoPrompt || generatedVideoPrompt}</p>
              </div>
            </div>

            <div className="rounded-lg border" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b px-3 py-3" style={{ borderColor: "var(--color-border-primary)" }}>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border"
                    style={{ borderColor: `${theme.colors.accentPrimary}55`, backgroundColor: `${theme.colors.accentPrimary}16`, color: theme.colors.accentPrimary }}
                  >
                    <Video size={16} />
                  </span>
                  <div>
                    <div className="text-sm font-bold">生成视频</div>
                    <div className="text-xs" style={{ color: "var(--color-text-secondary)" }}>可直接按招式描述生成；有参考图/视频时自动作为参考素材。</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleGenerateVideo}
                  disabled={videoGenerating}
                  className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-full px-4 text-sm font-bold text-[var(--color-accent-contrast)] disabled:opacity-50"
                  style={{ backgroundColor: theme.colors.accentPrimary }}
                >
                  {videoGenerating ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                  生成视频
                </button>
              </div>

              <div className="grid gap-3 p-3 xl:grid-cols-[minmax(0,1fr)_320px]">
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
                    <label className="space-y-1">
                      <span className="text-2xs font-bold" style={{ color: "var(--color-text-secondary)" }}>模型</span>
                      <AppSelect
                        value={videoModel}
                        onChange={e => setVideoModel(e.target.value)}
                        className="h-9 w-full rounded-lg border px-2 text-xs outline-none"
                        style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}
                      >
                        {videoModelOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </AppSelect>
                    </label>
                    <label className="space-y-1">
                      <span className="text-2xs font-bold" style={{ color: "var(--color-text-secondary)" }}>清晰度</span>
                      <AppSelect
                        value={videoResolution}
                        onChange={e => setVideoResolution(e.target.value)}
                        className="h-9 w-full rounded-lg border px-2 text-xs outline-none"
                        style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}
                      >
                        {VIDEO_RESOLUTION_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
                      </AppSelect>
                    </label>
                    <label className="space-y-1">
                      <span className="text-2xs font-bold" style={{ color: "var(--color-text-secondary)" }}>比例</span>
                      <AppSelect
                        value={videoRatio}
                        onChange={e => setVideoRatio(e.target.value)}
                        className="h-9 w-full rounded-lg border px-2 text-xs outline-none"
                        style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}
                      >
                        {VIDEO_RATIO_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
                      </AppSelect>
                    </label>
                    <label className="space-y-1">
                      <span className="text-2xs font-bold" style={{ color: "var(--color-text-secondary)" }}>时长</span>
                      <AppSelect
                        value={videoDuration}
                        onChange={e => setVideoDuration(Number(e.target.value))}
                        className="h-9 w-full rounded-lg border px-2 text-xs outline-none"
                        style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}
                      >
                        {VIDEO_DURATION_OPTIONS.map(option => <option key={option} value={option}>{option}s</option>)}
                      </AppSelect>
                    </label>
                    <label className="flex h-full min-h-[50px] items-end gap-2 rounded-lg border px-3 py-2 text-xs font-bold" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
                      <input
                        type="checkbox"
                        checked={videoGenerateAudio}
                        onChange={e => setVideoGenerateAudio(e.target.checked)}
                      />
                      生成声音
                    </label>
                  </div>

                  <label className="block space-y-2">
                    <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>视频提示词</span>
                    <textarea
                      value={videoPromptDraft}
                      onChange={e => setVideoPromptDraft(e.target.value)}
                      rows={5}
                      className="w-full resize-none rounded-lg border px-3 py-2 text-xs leading-5 outline-none"
                      style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}
                    />
                  </label>

                  {videoError && (
                    <div className="flex items-start gap-2 rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--color-red-primary)", color: "var(--color-red-primary)", backgroundColor: "var(--color-red-light)" }}>
                      <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                      <span>{videoError}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="relative flex aspect-video items-center justify-center overflow-hidden rounded-lg border" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
                    {videoGenerating && (
                      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 backdrop-blur-sm" style={{ backgroundColor: "var(--overlay-light)" }}>
                        <Loader2 className="animate-spin" size={24} style={{ color: theme.colors.accentPrimary }} />
                        <span className="text-xs font-bold">{videoProgressText || "生成中"}</span>
                      </div>
                    )}
                    {currentVideo?.videoUrl ? (
                      <video src={currentVideo.videoUrl} controls className="h-full w-full object-contain animate-in fade-in duration-300" />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-center text-xs" style={{ color: "var(--color-text-secondary)" }}>
                        <Video size={30} />
                        <span>{currentImage || localMediaRefs.length ? "将按参考素材生成视频" : "可直接按招式描述生成视频"}</span>
                      </div>
                    )}
                  </div>
                  {currentVideo?.videoUrl && (
                    <div className="flex items-center justify-between gap-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                      <span className="truncate text-2xs" style={{ color: "var(--color-text-secondary)" }}>{currentVideo.resolution} · {currentVideo.ratio} · {currentVideo.duration}s</span>
                      <a
                        href={currentVideo.videoUrl}
                        download
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold"
                        style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-primary)" }}
                      >
                        <Download size={13} /> 下载
                      </a>
                    </div>
                  )}
                  {videoHistory.length > 0 && (
                    <div className="custom-scrollbar flex max-h-28 flex-col gap-1 overflow-y-auto">
                      {videoHistory.map(item => (
                        <button
                          key={item.taskId || item.id}
                          type="button"
                          onClick={() => setCurrentVideo(item)}
                          className="flex cursor-pointer items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-left text-2xs"
                          style={{
                            borderColor: currentVideo?.taskId === item.taskId ? theme.colors.accentPrimary : "var(--color-border-primary)",
                            backgroundColor: currentVideo?.taskId === item.taskId ? "var(--color-accent-light)" : "var(--color-bg-primary)",
                          }}
                        >
                          <span className="truncate">{new Date(item.completedAt || item.createdAt).toLocaleString()}</span>
                          <span style={{ color: "var(--color-text-secondary)" }}>{item.resolution}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <aside className="flex min-h-0 flex-col overflow-hidden rounded-lg border" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
          <div className="border-b p-3" style={{ borderColor: "var(--color-border-primary)" }}>
            <div className="grid grid-cols-3 gap-1 rounded-full p-1" style={{ backgroundColor: "var(--color-bg-primary)" }}>
              {RIGHT_TABS.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setRightTab(tab.id)}
                  className="h-8 cursor-pointer rounded-full text-xs font-bold transition-colors"
                  style={{
                    backgroundColor: rightTab === tab.id ? theme.colors.accentPrimary : "transparent",
                    color: rightTab === tab.id ? "#fff" : "var(--color-text-secondary)",
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {rightTab === "assets" && (
            <>
              <div className="flex items-center gap-2 border-b p-3" style={{ borderColor: "var(--color-border-primary)" }}>
                <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
                  <Search size={15} style={{ color: "var(--color-text-secondary)" }} />
                  <input
                    value={assetSearch}
                    onChange={e => setAssetSearch(e.target.value)}
                    placeholder="全局搜索资产"
                    className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                  />
                </div>
                <button type="button" onClick={loadAssets} className="cursor-pointer rounded-full p-2" style={{ color: "var(--color-text-secondary)" }}>
                  <RefreshCw size={15} className={assetLoading ? "animate-spin" : ""} />
                </button>
              </div>
              <div className="custom-scrollbar grid flex-1 content-start grid-cols-2 gap-2 overflow-y-auto p-3">
                {filteredAssets.map(asset => {
                  const active = selectedAssetKeys.includes(asset.key);
                  return (
                    <button
                      key={asset.key}
                      type="button"
                      onClick={() => toggleAsset(asset.key)}
                      className="group relative cursor-pointer overflow-hidden rounded-lg border text-left"
                      style={{
                        borderColor: active ? theme.colors.accentPrimary : "var(--color-border-primary)",
                        backgroundColor: "var(--color-bg-tertiary)",
                      }}
                    >
                      <div className="aspect-square overflow-hidden">
                        <img src={asset.url} alt={asset.name} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                      </div>
                      {active && (
                        <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full text-[var(--color-accent-contrast)]" style={{ backgroundColor: theme.colors.accentPrimary }}>
                          <Check size={13} />
                        </span>
                      )}
                      <div className="p-2">
                        <div className="flex items-center gap-1 text-2xs" style={{ color: theme.colors.accentPrimary }}>
                          <span>{asset.typeLabel}</span>
                          {asset.isVariant && <span>变体</span>}
                        </div>
                        <div className="truncate text-xs font-bold">{asset.name}</div>
                        {asset.parentName && <div className="truncate text-2xs" style={{ color: "var(--color-text-secondary)" }}>{asset.parentName}</div>}
                      </div>
                    </button>
                  );
                })}
                {!assetLoading && filteredAssets.length === 0 && (
                  <div className="col-span-2 rounded-lg border p-4 text-center text-xs" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}>
                    没有可用参考资产
                  </div>
                )}
              </div>
            </>
          )}

          {rightTab === "saved" && (
            <div className="custom-scrollbar flex-1 space-y-2 overflow-y-auto p-3">
              {savedPrompts.map(item => (
                <div
                  key={item.id}
                  className="rounded-lg border p-3"
                  style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <button type="button" onClick={() => handleUseSavedPrompt(item)} className="min-w-0 flex-1 cursor-pointer text-left">
                      <div className="truncate text-xs font-bold">{item.title || "已保存招式/特效"}</div>
                      <div className="mt-1 text-2xs" style={{ color: "var(--color-text-secondary)" }}>
                        {item.assetTypeLabel || ASSET_TYPE_OPTIONS.find(option => option.value === item.assetType)?.label || "资产"} · {EFFECT_MODE_OPTIONS.find(option => option.value === item.mode)?.label || "模式"} · {REFERENCE_STYLE_OPTIONS.find(option => option.value === item.referenceStyle)?.label || "参考图"}
                      </div>
                    </button>
                    <button type="button" onClick={() => handleRemoveSavedPrompt(item.id)} className="cursor-pointer rounded-full p-1" style={{ color: "var(--color-text-secondary)" }}>
                      <X size={14} />
                    </button>
                  </div>
                  <p className="mt-2 line-clamp-3 text-xs leading-5" style={{ color: "var(--color-text-secondary)" }}>{item.fixedRuleText || item.prompt}</p>
                </div>
              ))}
              {savedPrompts.length === 0 && (
                <div className="rounded-lg border p-4 text-center text-xs" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}>
                  暂无保存招式/特效
                </div>
              )}
            </div>
          )}

          {rightTab === "history" && (
            <div className="custom-scrollbar flex-1 space-y-2 overflow-y-auto p-3">
              {historyLoading && <div className="text-xs" style={{ color: "var(--color-text-secondary)" }}>加载中...</div>}
              {history.map(item => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setCurrentImage(isDreaminaProxySeedance20Provider(videoProvider)
                      ? getDreaminaProxyOriginalUrl(item) || item.url : item.url);
                    setCurrentVideoPrompt(item.parsedMetadata?.videoPrompt || "");
                    applyWorkshopMetadata(item.parsedMetadata || {});
                  }}
                  className="flex w-full cursor-pointer gap-2 rounded-lg border p-2 text-left transition-colors"
                  style={{
                    borderColor: item.url === currentImage ? theme.colors.accentPrimary : "var(--color-border-primary)",
                    backgroundColor: item.url === currentImage ? "var(--color-accent-light)" : "var(--color-bg-tertiary)",
                  }}
                >
                  <img src={item.thumbnailUrl || item.url} alt="" className="h-14 w-14 rounded-md object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-bold">{item.parsedMetadata?.modeLabel || item.parsedMetadata?.title || item.name || "参考图"}</div>
                    <div className="mt-1 truncate text-2xs" style={{ color: "var(--color-text-secondary)" }}>{item.parsedMetadata?.referenceStyleLabel || item.parsedMetadata?.boardLayout || getPublicVideoLabel(item.parsedMetadata?.provider, "")}</div>
                    <div className="mt-1 text-2xs" style={{ color: "var(--color-text-tertiary)" }}>{item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}</div>
                  </div>
                </button>
              ))}
              {!historyLoading && history.length === 0 && (
                <div className="rounded-lg border p-4 text-center text-xs" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}>
                  暂无生成历史
                </div>
              )}
            </div>
          )}
        </aside>
      </main>
    </div>
  );
};

export default FightActionWorkshop;
